(function () {
  'use strict';

  const data = window.CartMartStreetLegalData;
  if (!data) return;

  const progressSteps = [
    'Locating your area...',
    'Triangulating city, state, and municipality...',
    'Checking DMV and vehicle-agency guidance...',
    'Pressure-testing street-ready and conversion rules...',
    'Matching factory street-legal vehicles...'
  ];

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function norm(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function parseZip(zip) {
    return /^(\d{5})(?:-\d{4})?$/.test((zip || '').trim());
  }

  function escapeHtml(value) {
    return (value || '').toString().replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  async function resolveZip(zip) {
    const response = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
    if (!response.ok) throw new Error('We could not locate this ZIP in the service map.');
    const payload = await response.json();
    const place = (payload.places && payload.places[0]) || {};
    return {
      zip,
      stateAbbr: place.state_abbr || '',
      state: place.state || '',
      city: place['place name'] || ''
    };
  }

  function makeLocalSourceUrl(geo) {
    const query = `${geo.city} ${geo.state} official golf cart low speed vehicle ordinance`;
    return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
  }

  function mergeAnswer(baseAnswer, stateAnswer, localAnswer) {
    return { ...baseAnswer, ...(stateAnswer || {}), ...(localAnswer || {}) };
  }

  function buildJurisdiction(geo) {
    const state = data.states[geo.stateAbbr] || {
      label: geo.state || geo.stateAbbr,
      agencyUrl: makeLocalSourceUrl(geo),
      majorCities: []
    };
    const stateRule = data.verifiedStateRules[geo.stateAbbr] || {};
    const localKey = `${norm(geo.city)}|${geo.stateAbbr}`;
    const localRule = data.verifiedMunicipalRules[localKey] || {};
    const isMajorCity = state.majorCities.some((city) => norm(city) === norm(geo.city));

    return {
      state,
      stateRule,
      localRule,
      isMajorCity,
      factoryLsv: mergeAnswer(data.defaultPolicy.factoryLsv, stateRule.factoryLsv, localRule.factoryLsv),
      streetReady: mergeAnswer(data.defaultPolicy.streetReady, stateRule.streetReady, localRule.streetReady),
      conversion: mergeAnswer(data.defaultPolicy.conversion, stateRule.conversion, localRule.conversion),
      driver: mergeAnswer(data.defaultPolicy.driver, stateRule.driver, localRule.driver),
      officialSourceUrl: localRule.sourceUrl || stateRule.sourceUrl || state.agencyUrl,
      localSourceUrl: makeLocalSourceUrl(geo),
      reviewedAt: localRule.reviewedAt || stateRule.reviewedAt || data.reviewedAt
    };
  }

  function answerTone(status) {
    if (/^allowed|conditional/.test(status)) return 'cm-pill-ok';
    if (/required|review/.test(status)) return 'cm-pill-warning';
    return 'cm-pill-unknown';
  }

  function renderAnswer(label, answer) {
    return `
      <article class="cm-model">
        <h3>${escapeHtml(label)}</h3>
        <p><span class="cm-pill ${answerTone(answer.status)}">${escapeHtml(answer.status.replaceAll('_', ' '))}</span></p>
        <p>${escapeHtml(answer.summary)}</p>
      </article>`;
  }

  function renderIntentAnswer(intent, jurisdiction) {
    const answers = {
      own: {
        kicker: 'Is my current cart street legal?',
        title: 'First identify what the vehicle legally is',
        body: `${jurisdiction.factoryLsv.summary} If the cart lacks factory LSV certification, use the Street Ready and conversion answers below instead of assuming equipment alone makes it legal.`,
        checks: ['Find the 17-digit VIN and manufacturer certification label', 'Confirm title, registration, insurance, and required equipment', 'Confirm this vehicle class is allowed on the roads you plan to use']
      },
      buy: {
        kicker: 'What street-legal cart can I buy?',
        title: 'Start with a factory-certified LSV configuration',
        body: `${jurisdiction.factoryLsv.summary} The model list below is limited to factory-LSV families, but the exact unit and route still need verification.`,
        checks: ['Buy the factory LSV version, not a similarly named golf-cart version', 'Verify the VIN and certification label before delivery', 'Match registration and road-speed rules to the delivery ZIP']
      },
      upgrade: {
        kicker: 'Can I upgrade my cart to street legal?',
        title: 'Equipment and legal conversion are separate questions',
        body: jurisdiction.conversion.summary,
        checks: ['Confirm the state offers an inspection, VIN, title, or registration path', 'Use compliant glazing, belts, lighting, signals, mirrors, brakes, reflectors, and horn', 'Do not promise street legality until the required authority approves the vehicle']
      }
    };
    const answer = answers[intent] || answers.own;
    return `
      <section class="cm-card cm-intent-answer">
        <div class="cm-kicker">${escapeHtml(answer.kicker)}</div>
        <h2 class="cm-title">${escapeHtml(answer.title)}</h2>
        <p>${escapeHtml(answer.body)}</p>
        <ol>${answer.checks.map((check) => `<li>${escapeHtml(check)}</li>`).join('')}</ol>
      </section>`;
  }

  function renderResult(root, geo, jurisdiction, intent) {
    const cityCoverage = jurisdiction.localRule.sourceUrl
      ? 'Direct municipal source loaded'
      : jurisdiction.isMajorCity
        ? 'Major-city record loaded; direct ordinance review pending'
        : 'State fallback loaded; local ordinance review pending';
    const factoryModels = data.factoryLsvCatalog.map((name) => `
      <article class="cm-model"><h3>${escapeHtml(name)}</h3><p>Show only the factory-certified LSV configuration with its valid VIN and certification label.</p></article>`).join('');

    root.querySelector('.street-legal-output').innerHTML = `
      ${renderIntentAnswer(intent, jurisdiction)}
      <section class="cm-card cm-result" aria-live="polite">
        <div class="cm-kicker">Location snapshot</div>
        <h2 class="cm-title">${escapeHtml(geo.city)}, ${escapeHtml(geo.state)} (${escapeHtml(geo.zip)})</h2>
        <p><span class="cm-pill cm-pill-warning">${escapeHtml(cityCoverage)}</span></p>
        <p>Reviewed data version ${escapeHtml(data.version)}. Last source review: ${escapeHtml(jurisdiction.reviewedAt)}.</p>
      </section>

      <section class="cm-card">
        <div class="cm-kicker">Complete local answer</div>
        <div class="cm-models">
          ${renderAnswer('Factory Street Legal / LSV', jurisdiction.factoryLsv)}
          ${renderAnswer('Street Ready / non-VIN golf cart', jurisdiction.streetReady)}
          ${renderAnswer('Can this cart be converted?', jurisdiction.conversion)}
          ${renderAnswer('Driver age and license rules', jurisdiction.driver)}
        </div>
      </section>

      <section class="cm-card">
        <div class="cm-kicker">Official information</div>
        <p><a href="${escapeHtml(jurisdiction.state.agencyUrl)}" target="_blank" rel="noopener">Open the ${escapeHtml(jurisdiction.state.label)} vehicle agency</a></p>
        <p><a href="${escapeHtml(jurisdiction.officialSourceUrl)}" target="_blank" rel="noopener">Open the strongest verified source currently loaded</a></p>
        <p><a href="${escapeHtml(jurisdiction.localSourceUrl)}" target="_blank" rel="noopener">Search official local golf-cart and LSV rules for ${escapeHtml(geo.city)}</a></p>
      </section>

      <section class="cm-card">
        <div class="cm-kicker">Factory LSV recommendations</div>
        <p>These are model families, not blanket approvals. Confirm the exact unit has a 17-digit VIN, factory certification label, title eligibility, and required road equipment.</p>
        <div class="cm-models">${factoryModels}</div>
      </section>

      <section class="cm-card">
        <div class="cm-kicker">Street Ready equipment screen</div>
        <p>${escapeHtml(data.streetReadyEquipment.join(', '))}.</p>
        <p class="cm-disclaimer">Equipment is only the first screen. It does not replace official permission, inspection, titling, registration, insurance, driver, road-speed, HOA, or country-club rules.</p>
      </section>`;
  }

  async function runLookup(form, root) {
    const zip = form.querySelector('[data-zip]').value.trim();
    const intent = form.querySelector('input[name="streetLegalIntent"]:checked').value;
    const status = root.querySelector('.cm-step');
    const output = root.querySelector('.street-legal-output');
    const submit = form.querySelector('button[type="submit"]');

    if (!parseZip(zip)) {
      output.innerHTML = '<p class="cm-error">Use a valid US ZIP like 90210.</p>';
      return;
    }

    submit.disabled = true;
    output.innerHTML = '';
    try {
      for (const step of progressSteps) {
        status.textContent = step;
        await sleep(520);
      }
      const geo = await resolveZip(zip);
      renderResult(root, geo, buildJurisdiction(geo), intent);
    } catch (error) {
      output.innerHTML = `<p class="cm-error">${escapeHtml(error.message)}</p>`;
    } finally {
      status.textContent = '';
      submit.disabled = false;
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    const root = document.querySelector('.street-legal-widget');
    if (!root) return;

    root.innerHTML = `
      <div class="cm-container">
        <div class="cm-card">
          <p class="cm-kicker">Street Legal Guide</p>
          <h1 class="cm-title">Is it street legal in my area?</h1>
          <p class="cm-subtitle">Choose what you need to know, enter your ZIP, and get the clearest answer supported by the rules currently loaded.</p>
          <form class="cm-form" data-street-legal-form>
            <fieldset class="cm-question-set">
              <legend>What are you trying to figure out?</legend>
              <div class="cm-question-grid">
                <label class="cm-question"><input type="radio" name="streetLegalIntent" value="own" checked><span><strong>Is my cart street legal?</strong><small>Check the cart I already own.</small></span></label>
                <label class="cm-question"><input type="radio" name="streetLegalIntent" value="buy"><span><strong>What can I buy?</strong><small>Show factory street-legal options.</small></span></label>
                <label class="cm-question"><input type="radio" name="streetLegalIntent" value="upgrade"><span><strong>Can I upgrade mine?</strong><small>Check for a documented conversion path.</small></span></label>
              </div>
            </fieldset>
            <div class="cm-grid">
              <div class="cm-field"><label for="street-legal-zip">ZIP code*</label><input id="street-legal-zip" data-zip required inputmode="numeric" pattern="\\d{5}" placeholder="e.g. 90210"></div>
            </div>
            <p class="cm-step" aria-live="polite"></p>
            <div class="cm-field cm-full"><button type="submit">Run street legal check</button></div>
          </form>
        </div>
        <div class="street-legal-output"></div>
        <section class="cm-card cm-definition-section" aria-labelledby="street-legal-definition-heading">
          <div class="cm-kicker">The plain-English version</div>
          <h2 class="cm-title" id="street-legal-definition-heading">What does “street legal” actually mean?</h2>
          <p class="cm-subtitle">People often use “street legal” for several different permissions. The right answer depends on the vehicle class, the road, and the local authority.</p>
          <div class="cm-definition-grid">
            <article class="cm-definition">
              <span>01</span><h3>Factory low-speed vehicle</h3>
              <p>A factory-certified LSV is built for limited public-road use, generally travels more than 20 but no more than 25 mph, carries a VIN and certification label, and includes federally required equipment. It still needs state title, registration, insurance, and an allowed road.</p>
            </article>
            <article class="cm-definition">
              <span>02</span><h3>Locally permitted golf cart</h3>
              <p>A standard golf cart may be allowed on designated streets, paths, communities, or private roads without becoming an LSV. The city, county, state, HOA, or property owner decides where it can operate and what permit, equipment, and driver rules apply.</p>
            </article>
            <article class="cm-definition">
              <span>03</span><h3>Converted golf cart</h3>
              <p>Lights, belts, DOT glazing, signals, mirrors, brakes, reflectors, and a horn can be part of a conversion. They do not create legal status by themselves. A valid conversion requires a documented inspection, VIN, title, registration, or approval process recognized by that jurisdiction.</p>
            </article>
            <article class="cm-definition">
              <span>04</span><h3>Road and driver permission</h3>
              <p>Posted-road limits are not nationally uniform. Depending on the state and municipality, an allowed road may be posted at 25, 30, or 35 mph, while other roads remain prohibited. Driver age, license, ID, supervision, and passenger rules can also change locally.</p>
            </article>
          </div>
        </section>
        <p class="cm-disclaimer">Advisory information only. Rules can change and may differ by road, municipality, county, HOA, country club, vehicle configuration, and registration status. Confirm final use with the responsible authorities.</p>
      </div>`;

    root.querySelector('[data-street-legal-form]').addEventListener('submit', function (event) {
      event.preventDefault();
      runLookup(event.currentTarget, root);
    });
  });
})();
